import { useNostr } from '@nostrify/react';
import { useQuery } from '@tanstack/react-query';
import type { NostrEvent } from '@nostrify/nostrify';

export const FLAG_KIND = 32239;

export function isValidFlag(event: NostrEvent): boolean {
  if (event.kind !== FLAG_KIND) return false;
  const d = event.tags.find(([n]) => n === 'd')?.[1];
  const data = event.tags.find(([n]) => n === 'data')?.[1];
  const title = event.tags.find(([n]) => n === 'title')?.[1];
  const encoding = event.tags.find(([n]) => n === 'encoding')?.[1];
  if (!d || !data || !title) return false;
  if (encoding !== 'text' && encoding !== 'hex') return false;
  if (!/^[0-9a-f]*$/.test(data) || data.length % 2 !== 0) return false;
  return true;
}

export function useFlags(limit = 50) {
  const { nostr } = useNostr();
  return useQuery({
    queryKey: ['flags', limit],
    queryFn: async (c) => {
      const events = await nostr.query(
        [{ kinds: [FLAG_KIND], limit }],
        { signal: c.signal }
      );
      return events.filter(isValidFlag).sort((a, b) => b.created_at - a.created_at);
    },
    staleTime: 30_000,
  });
}

export function useFlagsByAuthor(pubkey: string | undefined) {
  const { nostr } = useNostr();
  return useQuery({
    queryKey: ['flags', 'author', pubkey],
    queryFn: async (c) => {
      if (!pubkey) return [];
      const events = await nostr.query(
        [{ kinds: [FLAG_KIND], authors: [pubkey], limit: 100 }],
        { signal: c.signal }
      );
      return events.filter(isValidFlag).sort((a, b) => b.created_at - a.created_at);
    },
    enabled: !!pubkey,
    staleTime: 30_000,
  });
}
